import { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  keyframes,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import CustomCard from "../../components/common/CustomCard";
import CustomInput from "../../components/common/CustomInput";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import { authApi } from "../../api/authApi";
import CMNB from "../../assets/images/merubot_logo.png";
import CustomButton from "../../components/common/CustomButton";

const RegisterPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "info",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    userType: "user",
    name: "",
    password: "",
    confirmPassword: "",
  });

  const showSnackbar = (message, type = "info") =>
    setSnackbar({ open: true, message, type });

  /* ---------------- Register Logic ---------------- */

  const handleRegister = async () => {
    if (
      !formData.name ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      return showSnackbar("⚠️ Please fill all fields!", "warning");
    }

    if (formData.password !== formData.confirmPassword) {
      return showSnackbar("❌ Passwords do not match!", "error");
    }

    setLoading(true);

    try {
      const payload = {
        full_name: formData.name,
        email: formData.email,
        password: formData.password,
        userType: formData.userType,
      };

      const response = await authApi.register(payload);
      const { status, message } = response || {};

      if (status >= 500) {
        showSnackbar(
          message || "❌ Server error during registration.",
          "error"
        );
        return;
      }

      if (status >= 400) {
        showSnackbar(
          message || "❌ Registration failed. Please check your details.",
          "error"
        );
        return;
      }

      if (status === 201) {
        showSnackbar(
          message || "✅ Registration successful!",
          "success"
        );

        setTimeout(() => navigate("/login"), 1500);
        return;
      }

      showSnackbar(
        "⚠️ Unexpected response. Please try again.",
        "warning"
      );
    } catch (error) {
      console.error("Registration error:", error);
      showSnackbar(
        "❌ Something went wrong during registration.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Animation ---------------- */

  const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  `;

  /* ---------------- UI ---------------- */

  return (
    <Box
      sx={{
        // minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.palette.background.default,
      }}
    >
      <CustomCard
        sx={{
          width: "100%",
          maxWidth: 360,
          textAlign: "center",
          p: 3,
          borderRadius: 3,
          background: theme.palette.background.paper,
          animation: `${fadeIn} 0.4s ease`,
          boxShadow: theme.shadows[6],
        }}
      >
        {/* Logo (optional - you imported but didn't use) */}
        <Box
          component="img"
          src={CMNB}
          alt="Logo"
          sx={{
            height: 70,
            mb: 1,
            objectFit: "contain",
            mx: "auto",
            display: "block",
          }}
        />

        {/* Header */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            mb: 0.5,
            color: theme.palette.primary.main,
          }}
        >
          MeruBot
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mb: 2,
            color: theme.palette.text.secondary,
            fontSize: "0.85rem",
          }}
        >
          Create your account
        </Typography>

        {/* Inputs */}
        <CustomInput
          label="Full Name"
          size="small"
          value={formData.name}
          onChange={(e) =>
            setFormData({ ...formData, name: e.target.value })
          }
        />

        <CustomInput
          label="Email Address"
          size="small"
          type="email"
          value={formData.email}
          onChange={(e) =>
            setFormData({ ...formData, email: e.target.value })
          }
        />

        <CustomInput
          label="Password"
          size="small"
          tooltip="Password must be at least 13 characters long and contain at least one uppercase letter, one lowercase letter, one special character and one number."
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={(e) =>
            setFormData({ ...formData, password: e.target.value })
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPassword(!showPassword)}
                  sx={{ color: theme.palette.primary.main }}
                >
                  {showPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <CustomInput
          label="Confirm Password"
          size="small"
          type={showConfirmPassword ? "text" : "password"}
          value={formData.confirmPassword}
          onChange={(e) =>
            setFormData({
              ...formData,
              confirmPassword: e.target.value,
            })
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  sx={{ color: theme.palette.primary.main }}
                >
                  {showConfirmPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Button */}
        <CustomButton
          onClick={handleRegister}
          disabled={loading}
          sx={{
            mt: 2,
            height: 40,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: "0.95rem",
            textTransform: "none",
          }}
        >
          {loading ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            "Register"
          )}
        </CustomButton>

        {/* Footer */}
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            color: theme.palette.text.secondary,
            fontSize: "0.8rem",
          }}
        >
          Already have an account?{" "}
          <Link
            href="/login"
            underline="hover"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
            }}
          >
            Login
          </Link>
        </Typography>
      </CustomCard>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() =>
          setSnackbar({ ...snackbar, open: false })
        }
      />
    </Box>
  );
};

export default RegisterPage;